import { useLoginStore } from '@/stores/login';
import { useCartStore } from '@/stores/cart';
import { useDoorStore } from '@/stores/door';
import { usePublicStore } from '@/stores/public';
import { TABLE_VIEW_TYPE } from '@/constants/table.js';

import { tryFallbackPaths } from '@/utils/read_config';
import { jumpRePages } from '@/utils/public';
import { ref } from 'vue';

// 请求管理器：用于存储正在进行的请求，支持中断重复请求
const requestManager = {
    // 存储格式: { [requestKey]: requestTask }
    tasks: {},

    // 生成请求唯一标识
    generateKey(url, method, data) {
        const dataStr = JSON.stringify(data || {});
        return `${method}:${url}:${dataStr}`;
    },

    // 添加请求任务
    add(key, task) {
        // 如果已有相同请求，先取消它
        if (this.tasks[key]) {
            this.abort(key);
        }
        this.tasks[key] = task;
    },

    // 取消指定请求
    abort(key) {
        if (this.tasks[key]) {
            try {
                this.tasks[key].abort();
            } catch (e) {
                console.warn('请求取消失败:', e);
            }
            delete this.tasks[key];
        }
    },

    // 移除请求任务（请求完成后调用）
    remove(key) {
        delete this.tasks[key];
    }
};

// 基础url
export let baseUrl = '';
// 设置基础url
export function setBaseUrl(url) {
    baseUrl = url;
    uni.setStorageSync('baseUrl', url);
}
// 环境枚举
export const AppEnvEnum = ref({
    0: 'https://saas-billiard.wfydiaozhatian.com', // 开发环境
    1: 'https://api.dwjtq.com', // 正式环境
    2: 'https://api-dwjtq.dianwj.com', //预发布
    3: 'http://192.168.3.133:9501', // 刘弢
    4: 'http://192.168.3.72:9501', // 董壮
    5: 'http://192.168.3.114:9501', // 申胜利
    6: 'http://192.168.3.69:9501', // 韩潮江
    7: 'http://192.168.3.183:9501', // 何超
    8: 'http://192.168.3.218:9501', // 石磊
    9: 'http://192.168.3.50:9501', // 赵志杨
    10: 'http://192.168.3.52:9501', // 刘凯
    11: 'http://192.168.3.144:9501', // 柯豪
    12: 'http://192.168.3.32:9501' // 王龙鹤
});
const env = import.meta.env.VITE_NODE_ENV || 'production';
if (env === 'development') {
    baseUrl = uni.getStorageSync('baseUrl') || AppEnvEnum.value[0];
} else {
    baseUrl = AppEnvEnum.value[0];
}
const getUrl = async () => {
    if (env != 'development') {
        baseUrl = (await tryFallbackPaths()) || AppEnvEnum.value[1];
    }
};

let init = true;

export const request = async (url, method = 'GET', data = {}, header = {}, loading = false) => {
    if (init) {
        await getUrl();
        await usePublicStore().getDeviceCode();
        init = false;
    }
    // #ifdef H5
    if (usePublicStore().isOfflineH5) {
        uni.$emit('showToast', { title: '网络已断开', type: 'none' });
    }
    // #endif
    if (loading) {
        uni.$emit('showLoading', '加载中...');
    }

    // 生成请求唯一标识
    const requestKey = requestManager.generateKey(url, method, data);

    return new Promise((resolve, reject) => {
        // 购物车
        const cart = useCartStore();
        const loginstore = useLoginStore();
        const publicStore = usePublicStore();
        header['Authorization'] = loginstore.loginToken;
        header['Channel'] = 'cashier';
        // header['Pos-device-code'] = uni.getSystemInfoSync().deviceId || '';
        header['Pos-device-code'] = publicStore.systemInfo.deviceCode || '';
        const query = {
            shop_id: loginstore.currentShopId || 0,
            ...data
        };

        let timeoutStep = 15 * 1000;
        if (header?.timeout) {
            timeoutStep = header?.timeout;
            delete header?.timeout;
        }
        // console.log('请求参数------', query);

        // 发起请求并保存 requestTask
        const requestTask = uni.request({
            url: baseUrl + url,
            method: method,
            data: query,
            timeout: timeoutStep,
            header: { ...header },

            success: async (res) => {
                // 请求完成，移除任务
                requestManager.remove(requestKey);
                // console.log('响应参数------', res);
                uni.$emit('hideLoading');
                const temp_data = res.data;
                const { code, message } = res.data;
                if (code == 401) {
                    uni.$emit('showToast', { title: '登录过期，请重新登录', type: 'none' });
                    loginstore.exitLogin();
                    return jumpRePages('/pages/login');
                }
                // 约定1002，零售收音机被删，需要重新登录
                if (code == 1002) {
                    loginstore.exitLogin();
                    return jumpRePages('/pages/login');
                }
                // 432 拦截：密码校验 + 重新请求原接口
                if (code == 432) {
                    // 保存当前请求的所有参数
                    const originalRequest = {
                        url: url,
                        method: method,
                        data: data,
                        header: header
                    };

                    const doVerify = async (errMsg = '') => {
                        try {
                            // 打开密码验证弹窗
                            const formData = await new Promise((resolve, reject) => {
                                uni.$emit('openVerifyPasswordPopup', {
                                    onSuccess: resolve,
                                    onCancel: reject,
                                    params: {
                                        type: temp_data?.data?.type,
                                        status: temp_data?.data?.status,
                                        errorMsg: errMsg,
                                        approval_id: temp_data?.data?.approval_id
                                    }
                                });
                            });

                            // 密码认证
                            if (formData.authType === '1') {
                                uni.showLoading({ title: '验证中...' });
                                // 1. 先校验密码
                                const verifyRes = await request('/_hotel/approval/verifyPwd', 'POST', formData);
                                uni.hideLoading();

                                if (verifyRes.code === 200) {
                                    // 2. 校验成功 → 重新请求之前被拦截的接口
                                    let url = verifyRes?.data?.payload?.url || originalRequest.url;
                                    let method = verifyRes?.data?.payload?.method || originalRequest.method;
                                    let data = { ...verifyRes?.data?.payload?.body, approval_id: verifyRes?.data?.id };
                                    let header = { ...originalRequest.header, 'approval-id': verifyRes?.data?.id };
                                    const retryRes = await request(url, method, data, header, true);
                                    if (retryRes?.code == 200) {
                                        // 3. 关闭密码弹窗
                                        uni.$emit('closeVerifyPasswordPopup');
                                        // 4. 把最新数据返回给业务调用方
                                        resolve(retryRes);
                                    }
                                    return;
                                } else {
                                    // 验证失败，重新弹窗
                                    doVerify(verifyRes.message || '验证失败');
                                }
                                return;
                            }

                            // 权限申请
                            // if (formData.authType === '2') {
                            //     uni.showLoading({ title: '提交申请...' });
                            //     const applyRes = await request('/api/applyPermission', 'POST', formData);
                            //     uni.hideLoading();

                            //     if (!applyRes.data?.requestId) {
                            //         doVerify('申请提交失败');
                            //         return;
                            //     }
                            //     if (typeof verifyPopupRef !== 'undefined' && verifyPopupRef.value) {
                            //         verifyPopupRef.value.startPolling(applyRes.data.requestId, (success) => {
                            //             if (success) {
                            //                 uni.$emit('closeVerifyPasswordPopup');
                            //                 resolve(temp_data);
                            //             } else {
                            //                 doVerify('审核失败');
                            //             }
                            //         });
                            //     } else {
                            //         uni.$emit('showToast', { title: '轮询组件未找到', type: 'none' });
                            //         reject(temp_data);
                            //     }
                            // }
                        } catch (e) {
                            console.error('验证异常：', e);
                            // try {const cancel =
                            await request('/_hotel/approval/cancelApprove', 'POST', { ...e });
                            // } catch {
                            //     console.log(cancel)
                            // }
                            // 关闭弹窗
                            uni.$emit('closeVerifyPasswordPopup');
                            reject(temp_data);
                            uni.$emit('showToast', { title: '取消验证', type: 'none' });
                        } finally {
                            uni.hideLoading();
                        }
                    };

                    doVerify('');
                    return;
                }
                if (code == 1000) {
                    cart.clearAll('normal');
                    uni.$emit('showToast', { title: message, type: 'none' });
                    useDoorStore().setTableType(TABLE_VIEW_TYPE.INDEX);
                    reject(res.data);
                    return;
                }
                //  1001 ,为在线支付中的，特殊处理支付中
                if (code == 200 || code == 1001) {
                    resolve(res.data);
                    return;
                }
                if (data?.goods) {
                    resolve(res.data);
                    return;
                }
                uni.$emit('showToast', {
                    title: message || '请求失败',
                    type: 'error',
                    duration: 3000
                });
                setTimeout(() => {
                    uni.hideToast();
                    resolve(res.data);
                }, 1500);
            },

            fail: (err) => {
                // 请求失败，移除任务
                requestManager.remove(requestKey);
                uni.$emit('hideLoading');
                console.log('request fail ----', err);
                const result = { code: 500, msg: '获取数据失败' };
                reject(result);
            },

            complete: () => {
                // 请求完成（无论成功失败），移除任务
                requestManager.remove(requestKey);
                uni.hideLoading();
            }
        });

        // 添加到请求管理器（会自动取消相同的未完成请求）
        requestManager.add(requestKey, requestTask);
    });
};

export const uploadFun = (tempFilePath, header = {}) => {
    return new Promise((resolve, reject) => {
        header['Access-Hotel'] = uni.getStorageSync('hid') || '1013';
        header['Authorization'] = uni.getStorageSync('token') || '';

        uni.uploadFile({
            url: baseUrl + '/upload',
            filePath: tempFilePath,
            name: 'file',
            formData: { business: 'chat', file: tempFilePath },
            header: { ...header },
            success: function (uploadRes) {
                const { data } = JSON.parse(uploadRes.data);
                if (uploadRes.statusCode === 200) resolve(data);
            },
            fail: function (err) {
                uni.$emit('showToast', { title: '请检查网络', type: 'none' });
                reject(err);
            }
        });
    });
};

export const assets_url = 'https://oss-hongzhong.kstxyl.com/saas_billiard/app_static/';
